import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Badge, Box, Button, Divider, Flex, Image, SimpleGrid, Stack, Text} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import {useCustomToast} from "components/CustomToast/CustomToast";
import {boatService} from "services/boatService";
import {ROUTES} from "constants/routes-path";
import {FaFilePdf} from "react-icons/fa";
import {CUSTOMER_LOGO} from "../../../constants/config";
import {
    acidenteLabels,
    caracteristicaDaPistaLabels,
    condicaoDoTempoLabels,
    defaultBoat,
    getSelectedLabels,
    luminosidadeLabels,
    pistaLabels,
    sinalizacaoLabels,
    tipoDaPistaLabels
} from "../../../utils/boat-utils";

function BoatDetails() {
    const {id} = useParams();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const showToast = useCustomToast();

    const [boat, setBoat] = useState(defaultBoat)

    const handleExport = async () => {
        try {
            const blob = await boatService.downloadPdf(id, CUSTOMER_LOGO);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${boat.boatNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            showToast({title: t(err), status: "error"});
        }
    }

    const {data, images, videos} = boat
    const {local, acidente, condicoes, veiculos, versoes, observacoes, desenho} = data
    const {condicaoDoTempo, luminosidade, sinalizacao, tipoDaPista, caracteristicaDaPista, pista} = condicoes
    const {socorrista, agenteTransito, agentesEnvolvidos} = observacoes

    const naturezaOcorrencia = getSelectedLabels(acidente, acidenteLabels)
    const condicoesDoTempo = getSelectedLabels(condicaoDoTempo, condicaoDoTempoLabels)
    const condicoesLuminosidade = getSelectedLabels(luminosidade, luminosidadeLabels)
    const condicoesSinalizacao = getSelectedLabels(sinalizacao, sinalizacaoLabels)
    const tiposDePista = getSelectedLabels(tipoDaPista, tipoDaPistaLabels)
    const caracteristicasPista = getSelectedLabels(caracteristicaDaPista, caracteristicaDaPistaLabels)
    const condicoesDaPista = getSelectedLabels(pista, pistaLabels)

    useEffect(() => {
        if (id) {
            fetchBoat(id);
        }
    }, [id]);

    const fetchBoat = async (id) => {
        try {
            const boatResponse = await boatService.find(id);
            setBoat(boatResponse);
        } catch (err) {
            showToast({title: t(err), status: "error"});
        }
    };

    return (
        <Flex direction="column" pt={{sm: "125px", lg: "75px"}} w="100%">
            <Card px="0px" pt="10px" mb="24px" bg="rgba(255, 255, 255, 0.7)">
                <CardHeader px="22px" mb="24px">
                    <Flex direction="column">
                        <Flex justifyContent="flex-start" gap="3">
                            <Button
                                variant="light"
                                fontSize="sm"
                                alignSelf="flex-end"
                                mt="24px"
                                w="125px"
                                onClick={() => navigate(ROUTES.boatsPath)}
                            >
                                {t("system.actions.back")}
                            </Button>
                            <Button
                                variant="dark"
                                fontSize="sm"
                                alignSelf="flex-end"
                                mt="24px"
                                w="125px"
                                leftIcon={<FaFilePdf/>}
                                onClick={handleExport}
                            >
                                {t("boat.actions.export-pdf")}
                            </Button>
                        </Flex>
                    </Flex>
                </CardHeader>

                <CardBody m="10px" pb="40px" bg="#fff" style={{borderRadius: "25px"}}>
                  <Box>
                    <Flex gap="8" justify="column" pt="20px" px="40px">
                        <Flex gap="4" justify="space-between" w="100%">
                            <Text fontSize="lg" fontWeight="bold">{t('boat.report.title')}</Text>
                            <Text fontSize="lg"
                                  fontWeight="bold">{t('boat.report.boat-number')} # {boat.boatNumber}</Text>
                        </Flex>
                    </Flex>
                    <Flex gap="8" justify="column" pt="20px" px="40px">
                        <Flex gap="4" justify="space-between" w="100%">
                            <Image
                                src={CUSTOMER_LOGO}
                                alt="Logo 2"
                                w="200px"
                                h="200px"
                                maxH="100px"
                                objectFit="contain"
                            />
                            <Text>{t('boat.report.date')}: {new Date(boat.createdAt).toLocaleString()}</Text>
                        </Flex>
                    </Flex>

                    <Flex gap="4" justify="center" py="20px">
                        <Divider borderColor="gray.300"/>
                    </Flex>

                    <Flex gap="4" justify="" px="40px">
                        <Flex gap="4" justify="flex-start" w="100%">
                            <SimpleGrid spacing={2} columns={{sm: 1, md: 2}}>
                                <Text fontWeight="bold">{t('boat.report.location')}</Text>
                                <Text></Text>
                                <Text>{t('boat.report.location')}:</Text>
                                <Text>{local?.local}</Text>
                                <Text>{t('boat.report.region')}:</Text>
                                <Text>{local?.bairro}</Text>
                                <Text>{t('boat.report.city')}/{t('boat.report.state')}:</Text>
                                <Text>{`${local?.cidade}/${local?.uf}`}</Text>
                                <Text>{t('boat.report.reference')}:</Text>
                                <Text>{local?.pontoReferencia || 'N/A'}</Text>
                                <Text>{t('boat.report.accident-date')}:</Text>
                                <Text>{new Date(local?.dataHoraAcidente).toLocaleString()}</Text>
                            </SimpleGrid>
                        </Flex>
                        <Flex gap="4" justify="flex-start" w="100%">
                            <SimpleGrid columns={1} spacing={2}>
                                <Text fontWeight="bold">{t('boat.report.nature')}</Text>
                                <Box>
                                    {naturezaOcorrencia?.length === 0 && <Badge>N/A</Badge>}
                                    {naturezaOcorrencia?.map((v, index) => (
                                        <Badge key={`natureza-${index}`} variant="solid" colorScheme='purple' mr="5px">
                                            {v}
                                        </Badge>
                                    ))}
                                </Box>
                                <Text fontWeight="bold">{t('boat.report.weather-conditions')}</Text>
                                <Box>
                                    {condicoesDoTempo?.length === 0 && <Badge>N/A</Badge>}
                                    {condicoesDoTempo?.map((v, index) => (
                                        <Badge key={`condicoes-tempo-${index}`} variant="solid" colorScheme='purple'
                                               mr="5px">
                                            {v}
                                        </Badge>
                                    ))}
                                </Box>
                                <Text fontWeight="bold">{t('boat.report.light')}</Text>
                                <Box>
                                    {condicoesLuminosidade?.length === 0 && <Badge>N/A</Badge>}
                                    {condicoesLuminosidade?.map((v, index) => (
                                        <Badge key={`luminosidade-${index}`} variant="solid" colorScheme='purple'
                                               mr="5px">
                                            {v}
                                        </Badge>
                                    ))}
                                </Box>
                                <Text fontWeight="bold">{t('boat.report.sign')}</Text>
                                <Box>
                                    {condicoesSinalizacao?.length === 0 && <Badge>N/A</Badge>}
                                    {condicoesSinalizacao?.map((v, index) => (
                                        <Badge key={`sinalizacao-${index}`} variant="solid" colorScheme='purple'
                                               mr="5px">
                                            {v}
                                        </Badge>
                                    ))}
                                </Box>
                            </SimpleGrid>
                        </Flex>
                        <Flex gap="4" justify="flex-start" w="100%">
                            <SimpleGrid columns={1} spacing={2}>
                                <Text fontWeight="bold">{t('boat.report.road-type')}</Text>
                                <Box>
                                    {tiposDePista?.length === 0 && <Badge>N/A</Badge>}
                                    {tiposDePista?.map((v, index) => (
                                        <Badge key={`tipo-pista-${index}`} variant="solid" colorScheme='purple'
                                               mr="5px">
                                            {v}
                                        </Badge>
                                    ))}
                                </Box>
                                <Text fontWeight="bold">{t('boat.report.road-style')}</Text>
                                <Box>
                                    {caracteristicasPista?.length === 0 && <Badge>N/A</Badge>}
                                    {caracteristicasPista?.map((v, index) => (
                                        <Badge key={`caracteristicas-pista-${index}`} variant="solid"
                                               colorScheme='purple' mr="5px">
                                            {v}
                                        </Badge>
                                    ))}
                                </Box>
                                <Text fontWeight="bold">{t('boat.report.road-conditions')}</Text>
                                <Box>
                                    {condicoesDaPista?.length === 0 && <Badge>N/A</Badge>}
                                    {condicoesDaPista?.map((v, index) => (
                                        <Badge key={`condicoes-pista-${index}`} variant="solid" colorScheme='purple'
                                               mr="5px">
                                            {v}
                                        </Badge>
                                    ))}
                                </Box>
                            </SimpleGrid>
                        </Flex>
                    </Flex>

                    <Flex gap="4" justify="center" py="20px">
                        <Divider borderColor="gray.300"/>
                    </Flex>

                    <Flex gap="4" justify="flex-start" px="40px">
                        <SimpleGrid columns={1} spacing={2}>
                            <Text fontWeight="bold">{t('boat.report.vehicles')}</Text>
                        </SimpleGrid>
                    </Flex>

                    {veiculos?.map((v, index) => (
                        <Flex key={`flex-vehicle-${index}`} gap="4" justify="flex-start" px="40px" mt="20px">
                            <SimpleGrid spacing={2} flex='1' columns={{sm: 1, md: 2}}>
                                <SimpleGrid spacing={2} columns={{sm: 1, md: 2}}>
                                    <Text fontWeight="bold">{t('boat.report.vehicle.title')} {index + 1}</Text>
                                    <Text></Text>
                                    <Text>{t('boat.report.vehicle.plate')}:</Text>
                                    <Box><Badge variant="solid" colorScheme='purple'>{v.placa}</Badge></Box>
                                    <Text>{t('boat.report.vehicle.owner')}:</Text>
                                    <Text>{v.proprietario || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.brand')}/{t('boat.report.vehicle.model')}:</Text>
                                    <Text>{v.marcaModelo || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.type')}:</Text>
                                    <Text>{v.tipo || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.color')}:</Text>
                                    <Text>{v.cor || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.fuel')}:</Text>
                                    <Text>{v.combustivel || '--'}</Text>
                                    <Text>{t('boat.report.state')}:</Text>
                                    <Text>{v.uf || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.number-people')}:</Text>
                                    <Text>{v.numeroOcupantes || '--'}</Text>
                                </SimpleGrid>
                                <SimpleGrid spacing={2} columns={{sm: 1, md: 2}} flex='1'>
                                    <Text fontWeight="bold">{t('boat.report.vehicle.driver.title')}</Text>
                                    <Text></Text>
                                    <Text>{t('boat.report.vehicle.driver.name')}:</Text>
                                    <Text>{v.condutor?.nome || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.driver.birthdate')}:</Text>
                                    <Text>{v.condutor?.dataNascimento || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.driver.address')}:</Text>
                                    <Text>{v.condutor?.endereco || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.driver.phone')}:</Text>
                                    <Text>{v.condutor?.telefone || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.driver.id')}:</Text>
                                    <Text>{v.condutor?.cpf || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.driver.license')}:</Text>
                                    <Text>{v.condutor?.cnh || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.driver.expiration')}:</Text>
                                    <Text>{v.condutor?.validadeCnh || '--'}</Text>
                                    <Text>{t('boat.report.vehicle.driver.category')}:</Text>
                                    <Text>{v.condutor?.categoriaCnh || '--'}</Text>
                                </SimpleGrid>
                            </SimpleGrid>
                        </Flex>
                    ))}

                    {versoes?.length > 0 && (
                        <>
                            <Flex gap="4" justify="center" py="20px">
                                <Divider borderColor="gray.300"/>
                            </Flex>

                            <Flex gap="4" justify="flex-start" px="40px">
                                <SimpleGrid columns={1} spacing={2}>
                                    <Text fontWeight="bold">{t('boat.report.versions')}</Text>
                                </SimpleGrid>
                            </Flex>

                            {versoes?.map((v, index) => (
                                <Flex key={`flex-versao-${index}`} gap="4" justify="flex-start" px="40px" mt="20px">
                                    <SimpleGrid spacing={2} flex='1' columns={{sm: 1, md: 2}}>
                                        <SimpleGrid spacing={2} columns={{sm: 1, md: 2}}>
                                            <Text fontWeight="bold">{t('boat.report.version.title')} {index + 1}</Text>
                                            <Text></Text>
                                            <Text>{t('boat.report.version.name')}:</Text>
                                            <Text>{v.nome || '--'}</Text>
                                            <Text>{t('boat.report.version.street')}/{t('boat.report.vehicle.model')}:</Text>
                                            <Text>{v.rua || '--'}</Text>
                                            <Text>{t('boat.report.version.direction')}:</Text>
                                            <Text>{v.sentido || '--'}</Text>
                                            <Text>{t('boat.report.version.title')}:</Text>
                                            <Text>{v.versao || '--'}</Text>
                                        </SimpleGrid>
                                    </SimpleGrid>
                                </Flex>
                            ))}
                        </>
                    )}

                    <Flex gap="4" justify="center" py="20px">
                        <Divider borderColor="gray.300"/>
                    </Flex>

                    <Flex gap="4" justify="flex-start" px="40px">
                        <SimpleGrid columns={1} spacing={2}>
                            <Text fontWeight="bold">{t('boat.report.observations')}</Text>
                        </SimpleGrid>
                    </Flex>

                    <Flex gap="4" justify="flex-start" px="40px">
                        <SimpleGrid spacing={2} flex='1' columns={{sm: 1, md: 2}} pt="15px">
                            <SimpleGrid spacing={2} columns={{sm: 1, md: 2}}>
                                <Text fontWeight="bold">{t('boat.report.observation.agent')}</Text>
                                <Text></Text>
                                <Text>{t('boat.report.observation.registration')}:</Text>
                                <Text>{agenteTransito.matricula}</Text>
                                <Text>{t('boat.report.observation.name')}:</Text>
                                <Text>{agenteTransito.nome}</Text>
                                <Text>{t('boat.report.observation.title')}:</Text>
                                <Text>{agenteTransito.observacao || '--'}</Text>
                                <Text fontWeight="bold">{t('boat.report.observation.rescuer')}</Text>
                                <Text></Text>
                                <Text>{t('boat.report.observation.name')}:</Text>
                                <Text>{socorrista.nome || '--'}</Text>
                                <Text>{t('boat.report.observation.id')}:</Text>
                                <Text>{socorrista.documentoIdentificacao || '--'}</Text>
                                <Text>{t('boat.report.observation.address')}:</Text>
                                <Text>{socorrista.endereco || '--'}</Text>
                                <Text>{t('boat.report.observation.title')}:</Text>
                                <Text>{socorrista.observacao || '--'}</Text>
                            </SimpleGrid>
                            <Stack spacing={2}>
                                {agentesEnvolvidos?.length > 0 &&
                                    <Text fontWeight="bold">{t('boat.report.agents')}</Text>}
                                {agentesEnvolvidos.map((agente, id) => (
                                    <Text key={`agente-${id}`}>{agente.matricula} - {agente.nome}</Text>
                                ))}
                            </Stack>
                        </SimpleGrid>
                    </Flex>

                    {images?.length > 0 && (
                        <>
                            <Flex gap="4" justify="center" py="20px">
                                <Divider borderColor="gray.300"/>
                            </Flex>

                            <Flex gap="4" justify="flex-start" px="40px">
                                <Text fontWeight="bold">{t('boat.report.images')}</Text>
                            </Flex>

                            <Flex gap="4" justify="flex-start" px="40px" pt="20px">
                                {images?.map((imageUrl, index) => (
                                    <img alt={`image-${index}`} style={{maxWidth: 250}} width='100%' height='100%'
                                         src={imageUrl}/>
                                ))}
                            </Flex>
                        </>
                    )}

                    {videos?.length > 0 && (
                        <>
                            <Flex gap="4" justify="center" py="20px">
                                <Divider borderColor="gray.300"/>
                            </Flex>

                            <Flex gap="4" justify="flex-start" px="40px">
                                <Text fontWeight="bold">{t('boat.report.videos')}</Text>
                            </Flex>

                            <Flex gap="4" justify="flex-start" px="40px" pt="20px">
                                {videos?.map((videoUrl, index) => (
                                    <>
                                        <video key={`video-${index}`} width={150} height={150} controls src={videoUrl}/>
                                    </>
                                ))}
                            </Flex>
                        </>
                    )}

                    {desenho?.image && (
                        <>

                            <Flex gap="4" justify="center" py="20px">
                                <Divider borderColor="gray.300"/>
                            </Flex>

                            <Flex gap="4" justify="flex-start" px="40px">
                                <Text fontWeight="bold">{t('boat.report.drawing')}</Text>
                            </Flex>

                            <Flex gap="4" justify="flex-start" px="40px" pt="20px">
                                {desenho?.image && (
                                    <img alt='Desenho' style={{maxWidth: 400, maxHeight: 400}}
                                         src={desenho.image}/>
                                )}
                                {!desenho?.image && 'Nenhum desenho foi provido.'}
                            </Flex>
                        </>
                    )}
                  </Box>
                </CardBody>
            </Card>
        </Flex>
    );
}

export default BoatDetails;